import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LargeTitle, Surface, Pill, Skeleton, EmptyState, AButton, Segmented, SearchInput } from "@/components/ios";
import { ClipboardCheck, ChevronRight, Camera, FileText, Plus } from "lucide-react";

const STATUS_TONE = { in_progress: "warning", report_drafted: "accent", completed: "success" };
const FILTERS = [
  { value: "all", label: "All" },
  { value: "in_progress", label: "In progress" },
  { value: "report_drafted", label: "Drafted" },
  { value: "completed", label: "Completed" },
];

export default function Inspections() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const nav = useNavigate();
  useEffect(() => { api.get("/inspections").then(({ data }) => setItems(data)); }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    const query = q.trim().toLowerCase();
    return items.filter((it) => {
      if (status !== "all" && it.status !== status) return false;
      if (!query) return true;
      return [it.property?.address, it.property?.suburb, it.report_type]
        .filter(Boolean).some((s) => s.toLowerCase().includes(query));
    });
  }, [items, q, status]);

  return (
    <div>
      <LargeTitle subtitle="Capture in progress and drafted reports"
        right={<AButton testid="new-inspection-btn" onClick={() => nav("/properties")}><Plus size={15} className="inline mr-1 -mt-0.5" /> New inspection</AButton>}>
        Inspections
      </LargeTitle>

      {items && items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <SearchInput testid="inspections-search" value={q} onChange={setQ} placeholder="Search by address, suburb, type…" />
          <Segmented testid="inspections-status-filter" value={status} onChange={setStatus} options={FILTERS} />
        </div>
      )}

      {!items ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : items.length === 0 ? (
        <Surface><EmptyState icon={ClipboardCheck} title="No inspections yet"
          subtitle="Open a property and start a photo/checklist inspection to see it here."
          action={<AButton testid="empty-new-inspection-btn" onClick={() => nav("/properties")}>Choose a property</AButton>} /></Surface>
      ) : filtered.length === 0 ? (
        <Surface><EmptyState icon={ClipboardCheck} title="No matching inspections" subtitle="Try a different search or filter." /></Surface>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => {
            const drafted = it.report;
            return (
              <Surface key={it.id} className="p-4 flex items-center gap-4 cursor-pointer active:scale-[0.995] transition-transform"
                data-testid={`inspection-row-${it.id}`}
                onClick={() => nav(drafted ? `/reports/${drafted.id}/review` : `/inspections/${it.id}/capture`)}>
                <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--accent-soft)" }}>
                  {drafted ? <FileText size={18} className="accent-text" /> : <Camera size={18} className="accent-text" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate" style={{ color: "var(--text)" }}>{it.property?.address}</div>
                  <div className="text-sm text-secondary capitalize">{it.report_type} · {it.property?.state} · {new Date(it.created_at).toLocaleDateString("en-AU")}</div>
                </div>
                <Pill tone={STATUS_TONE[it.status] || "neutral"}>{it.status.replace(/_/g, " ")}</Pill>
                <ChevronRight size={16} className="text-secondary" />
              </Surface>
            );
          })}
        </div>
      )}
    </div>
  );
}
